/**
 * Cliente IPC tipado. Envuelve un `Transport` y traduce las respuestas del puente
 * (`{ok,result,error}`) a un `Result` discriminado que la UI consume con manejo
 * explícito de carga/error.
 */
import type { Transport } from "./transport";
import { IPC_CHANNELS } from "./channels";
import type {
  ExecutiveOverviewDto, EngineOverviewDto, PeriodRange, ProductDevelopmentOverviewDto, KpiTargetsResult, AccountsSnapshot, ConsolidatedResult,
  OpenInvestigationInput, PdCandidateDto, SimulateInput, SimulationResultDto,
  CredentialsStatus, SaveCredentialsInput, CredentialTestResult, CredentialProvider,
  MlBeginConnectInput, MlBeginConnectResult, OnboardingStatus, BusinessConfig,
  MlAccountResult, AiAnswerResult, MlQuestionsResult, MlQuestionsDraftResult, MlAnswerResult, MlDataSpikeResult,
  MlCategoriesResult, MlBestsellersResult, TrendAnalysis, CaptureResult, TrackedResult, TrendHistory, ReviewIntelResult, WatchlistResult, WatchRunResult,
  BackupList, BackupCreateResult, BackupRestoreResult, BackupExportResult, NotificationsList,
  SyncConfig, SyncAllResult, ExportSaveResult, UpdateState,
  ImageAnalyzeResult, ImageGenerateResult, DescriptionResult,
  CreateProductInput, ProductAction, ActionResult, ProductCatalog, SkuInfo,
  CreateCustomerInput, CustomerAction, CreateSupplierInput, SupplierAction,
  CreateSaleInput, SalesAction, SalesCustomerList,
  CreateStockInput, InventoryAction,
  CreatePurchaseInput, PurchaseAction, PurchaseSupplierList,
  CreatePriceListInput, AddPriceInput, PriceListAction,
  CreateShipmentInput, ShipmentAction,
  CreatePublicationInput, PublicationAction,
  RegisterModelInput, ModelAction,
} from "./types";

export type IpcResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: { readonly code: string; readonly message: string } };

export class IpcClient {
  constructor(private readonly transport: Transport) {}

  private async request<T>(channel: string, payload?: unknown): Promise<IpcResult<T>> {
    const response = await this.transport.invoke<T>(channel, payload);
    if (response.ok && response.result !== undefined) {
      return { ok: true, value: response.result };
    }
    return {
      ok: false,
      error: response.error ?? { code: "unknown", message: "Respuesta inválida del backend" },
    };
  }

  getExecutiveOverview(range?: PeriodRange): Promise<IpcResult<ExecutiveOverviewDto>> {
    return this.request<ExecutiveOverviewDto>(IPC_CHANNELS.brainOverview, range ? { range } : undefined);
  }

  getEngineOverview(engineId: string, range?: PeriodRange): Promise<IpcResult<EngineOverviewDto>> {
    return this.request<EngineOverviewDto>(IPC_CHANNELS.engineOverview, { engineId, ...(range ? { range } : {}) });
  }

  /** Multi-cuenta (perfiles de Mercado Libre). */
  getAccounts(): Promise<IpcResult<AccountsSnapshot>> { return this.request<AccountsSnapshot>(IPC_CHANNELS.accountsList); }
  addAccount(nickname: string): Promise<IpcResult<AccountsSnapshot>> { return this.request<AccountsSnapshot>(IPC_CHANNELS.accountsAdd, { nickname }); }
  setActiveAccount(id: string): Promise<IpcResult<AccountsSnapshot>> { return this.request<AccountsSnapshot>(IPC_CHANNELS.accountsSetActive, { id }); }
  renameAccount(id: string, nickname: string): Promise<IpcResult<AccountsSnapshot>> { return this.request<AccountsSnapshot>(IPC_CHANNELS.accountsRename, { id, nickname }); }
  removeAccount(id: string): Promise<IpcResult<AccountsSnapshot>> { return this.request<AccountsSnapshot>(IPC_CHANNELS.accountsRemove, { id }); }
  getConsolidated(): Promise<IpcResult<ConsolidatedResult>> { return this.request<ConsolidatedResult>(IPC_CHANNELS.accountsConsolidated); }

  /** Analytics: KPIs con metas configurables. */
  getAnalyticsTargets(range?: PeriodRange): Promise<IpcResult<KpiTargetsResult>> {
    return this.request<KpiTargetsResult>(IPC_CHANNELS.analyticsTargetsList, range ? { range } : undefined);
  }
  setAnalyticsTarget(key: string, target: number): Promise<IpcResult<KpiTargetsResult>> {
    return this.request<KpiTargetsResult>(IPC_CHANNELS.analyticsTargetsSet, { key, target });
  }

  getProductDevelopmentOverview(): Promise<IpcResult<ProductDevelopmentOverviewDto>> {
    return this.request<ProductDevelopmentOverviewDto>(IPC_CHANNELS.productDevelopmentOverview);
  }

  openInvestigation(input: OpenInvestigationInput): Promise<IpcResult<PdCandidateDto>> {
    return this.request<PdCandidateDto>(IPC_CHANNELS.productDevelopmentOpen, input);
  }

  simulateInvestigation(input: SimulateInput): Promise<IpcResult<SimulationResultDto>> {
    return this.request<SimulationResultDto>(IPC_CHANNELS.productDevelopmentSimulate, input);
  }

  getCredentialsStatus(): Promise<IpcResult<CredentialsStatus>> {
    return this.request<CredentialsStatus>(IPC_CHANNELS.credentialsStatus);
  }

  saveCredentials(input: SaveCredentialsInput): Promise<IpcResult<CredentialsStatus>> {
    return this.request<CredentialsStatus>(IPC_CHANNELS.credentialsSave, input);
  }

  testCredentials(provider: CredentialProvider): Promise<IpcResult<CredentialTestResult>> {
    return this.request<CredentialTestResult>(IPC_CHANNELS.credentialsTest, { provider });
  }

  mlBeginConnect(input: MlBeginConnectInput): Promise<IpcResult<MlBeginConnectResult>> {
    return this.request<MlBeginConnectResult>(IPC_CHANNELS.mlBeginConnect, input);
  }

  mlCompleteConnect(code: string): Promise<IpcResult<CredentialTestResult>> {
    return this.request<CredentialTestResult>(IPC_CHANNELS.mlCompleteConnect, { code });
  }

  getOnboardingStatus(): Promise<IpcResult<OnboardingStatus>> {
    return this.request<OnboardingStatus>(IPC_CHANNELS.onboardingStatus);
  }

  completeOnboarding(): Promise<IpcResult<OnboardingStatus>> {
    return this.request<OnboardingStatus>(IPC_CHANNELS.onboardingComplete);
  }

  getBusinessConfig(): Promise<IpcResult<BusinessConfig>> {
    return this.request<BusinessConfig>(IPC_CHANNELS.businessConfigGet);
  }

  saveBusinessConfig(input: Partial<BusinessConfig>): Promise<IpcResult<BusinessConfig>> {
    return this.request<BusinessConfig>(IPC_CHANNELS.businessConfigSave, input);
  }

  getMercadoLibreAccount(): Promise<IpcResult<MlAccountResult>> {
    return this.request<MlAccountResult>(IPC_CHANNELS.mlAccount);
  }

  askAi(prompt: string): Promise<IpcResult<AiAnswerResult>> {
    return this.request<AiAnswerResult>(IPC_CHANNELS.aiAsk, { prompt });
  }

  /** Diagnóstico de fuentes de datos de ML para el módulo Tendencias (solo lectura). */
  mlDataSpike(): Promise<IpcResult<MlDataSpikeResult>> {
    return this.request<MlDataSpikeResult>(IPC_CHANNELS.mlDataSpike);
  }

  /** Tendencias ML: categorías de primer nivel. */
  getTrendCategories(): Promise<IpcResult<MlCategoriesResult>> {
    return this.request<MlCategoriesResult>(IPC_CHANNELS.trendCategories);
  }
  /** Tendencias ML: subcategorías de una categoría. */
  getTrendSubcategories(categoryId: string): Promise<IpcResult<MlCategoriesResult>> {
    return this.request<MlCategoriesResult>(IPC_CHANNELS.trendSubcategories, { categoryId });
  }
  /** Tendencias ML: más vendidos (Highlights) de una categoría, con detalle real. */
  getTrendBestSellers(categoryId: string): Promise<IpcResult<MlBestsellersResult>> {
    return this.request<MlBestsellersResult>(IPC_CHANNELS.trendBestSellers, { categoryId });
  }
  /** Tendencias ML: más vendidos EN VIVO + Δ vs. captura anterior (momentum). */
  analyzeTrends(categoryId: string): Promise<IpcResult<TrendAnalysis>> {
    return this.request<TrendAnalysis>(IPC_CHANNELS.trendAnalyze, { categoryId });
  }
  /** Tendencias ML: guarda la foto de hoy del ranking de la categoría. */
  captureTrendSnapshot(categoryId: string): Promise<IpcResult<CaptureResult>> {
    return this.request<CaptureResult>(IPC_CHANNELS.trendCapture, { categoryId });
  }
  /** Tendencias ML: categorías con histórico. */
  getTrackedTrends(): Promise<IpcResult<TrackedResult>> {
    return this.request<TrackedResult>(IPC_CHANNELS.trendTracked);
  }
  /** Tendencias ML: serie temporal + velocidad de una categoría (Fase 6). */
  getTrendHistory(categoryId: string): Promise<IpcResult<TrendHistory>> {
    return this.request<TrendHistory>(IPC_CHANNELS.trendHistory, { categoryId });
  }
  /** Tendencias ML: opiniones de una publicación → quejas y huecos de mercado (Fase 8). */
  getReviewIntel(itemId: string, title?: string): Promise<IpcResult<ReviewIntelResult>> {
    return this.request<ReviewIntelResult>(IPC_CHANNELS.trendReviews, { itemId, title });
  }
  /** Vigilancia (Fase 12): watchlist + corrida manual. */
  getWatchlist(): Promise<IpcResult<WatchlistResult>> {
    return this.request<WatchlistResult>(IPC_CHANNELS.trendWatchlist);
  }
  addToWatchlist(categoryId: string, name: string): Promise<IpcResult<WatchlistResult>> {
    return this.request<WatchlistResult>(IPC_CHANNELS.trendWatchAdd, { categoryId, name });
  }
  removeFromWatchlist(categoryId: string): Promise<IpcResult<WatchlistResult>> {
    return this.request<WatchlistResult>(IPC_CHANNELS.trendWatchRemove, { categoryId });
  }
  runWatch(): Promise<IpcResult<WatchRunResult>> {
    return this.request<WatchRunResult>(IPC_CHANNELS.trendWatchRun);
  }

  /** Preguntas reales de compradores sin responder (solo lectura). */
  getMlQuestions(limit?: number): Promise<IpcResult<MlQuestionsResult>> {
    return this.request<MlQuestionsResult>(IPC_CHANNELS.mlQuestionsList, limit !== undefined ? { limit } : undefined);
  }

  /** La IA redacta borradores de respuesta (no se envían a Mercado Libre). */
  draftMlQuestionReplies(limit?: number): Promise<IpcResult<MlQuestionsDraftResult>> {
    return this.request<MlQuestionsDraftResult>(IPC_CHANNELS.mlQuestionsDraft, limit !== undefined ? { limit } : undefined);
  }

  /** Envía la respuesta a Mercado Libre (acción hacia afuera; la UI confirma antes). */
  answerMlQuestion(questionId: string, text: string): Promise<IpcResult<MlAnswerResult>> {
    return this.request<MlAnswerResult>(IPC_CHANNELS.mlQuestionAnswer, { questionId, text });
  }

  getAiRecommendation(): Promise<IpcResult<AiAnswerResult>> {
    return this.request<AiAnswerResult>(IPC_CHANNELS.brainAiRecommendation);
  }

  createBackup(): Promise<IpcResult<BackupCreateResult>> {
    return this.request<BackupCreateResult>(IPC_CHANNELS.backupCreate);
  }

  listBackups(): Promise<IpcResult<BackupList>> {
    return this.request<BackupList>(IPC_CHANNELS.backupList);
  }

  restoreBackup(name: string): Promise<IpcResult<BackupRestoreResult>> {
    return this.request<BackupRestoreResult>(IPC_CHANNELS.backupRestore, { name });
  }

  exportBackup(): Promise<IpcResult<BackupExportResult>> {
    return this.request<BackupExportResult>(IPC_CHANNELS.backupExport);
  }

  importBackup(): Promise<IpcResult<BackupRestoreResult>> {
    return this.request<BackupRestoreResult>(IPC_CHANNELS.backupImport);
  }

  listNotifications(): Promise<IpcResult<NotificationsList>> {
    return this.request<NotificationsList>(IPC_CHANNELS.notificationsList);
  }
  markAllNotificationsRead(): Promise<IpcResult<NotificationsList>> {
    return this.request<NotificationsList>(IPC_CHANNELS.notificationsMarkAllRead);
  }
  dismissNotification(id: string): Promise<IpcResult<NotificationsList>> {
    return this.request<NotificationsList>(IPC_CHANNELS.notificationsDismiss, { id });
  }
  clearNotifications(): Promise<IpcResult<NotificationsList>> {
    return this.request<NotificationsList>(IPC_CHANNELS.notificationsClear);
  }

  getSyncConfig(): Promise<IpcResult<SyncConfig>> {
    return this.request<SyncConfig>(IPC_CHANNELS.syncConfigGet);
  }
  saveSyncConfig(input: Partial<SyncConfig>): Promise<IpcResult<SyncConfig>> {
    return this.request<SyncConfig>(IPC_CHANNELS.syncConfigSave, input);
  }
  syncAll(): Promise<IpcResult<SyncAllResult>> {
    return this.request<SyncAllResult>(IPC_CHANNELS.marketplaceSyncAll);
  }

  saveExport(defaultName: string, content: string): Promise<IpcResult<ExportSaveResult>> {
    return this.request<ExportSaveResult>(IPC_CHANNELS.exportSave, { defaultName, content });
  }

  saveImage(defaultName: string, base64: string): Promise<IpcResult<ExportSaveResult>> {
    return this.request<ExportSaveResult>(IPC_CHANNELS.exportSaveImage, { defaultName, base64 });
  }

  analyzeProductImage(imageBase64: string, mime: string): Promise<IpcResult<ImageAnalyzeResult>> {
    return this.request<ImageAnalyzeResult>(IPC_CHANNELS.productImagesAnalyze, { imageBase64, mime });
  }

  generateProductImage(prompt: string, size?: string): Promise<IpcResult<ImageGenerateResult>> {
    return this.request<ImageGenerateResult>(IPC_CHANNELS.productImagesGenerate, { prompt, ...(size ? { size } : {}) });
  }

  generateProductDescription(input: { title: string; context?: string; country?: string }): Promise<IpcResult<DescriptionResult>> {
    return this.request<DescriptionResult>(IPC_CHANNELS.productDescriptionGenerate, input);
  }

  getUpdateStatus(): Promise<IpcResult<UpdateState>> {
    return this.request<UpdateState>(IPC_CHANNELS.updateStatus);
  }
  checkForUpdates(): Promise<IpcResult<UpdateState>> {
    return this.request<UpdateState>(IPC_CHANNELS.updateCheck);
  }
  installUpdate(): Promise<IpcResult<{ installing: boolean }>> {
    return this.request<{ installing: boolean }>(IPC_CHANNELS.updateInstall);
  }

  createProduct(input: CreateProductInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.productCreate, input);
  }

  getProductCatalog(): Promise<IpcResult<ProductCatalog>> {
    return this.request<ProductCatalog>(IPC_CHANNELS.productCatalog);
  }

  getSkuInfo(sku: string): Promise<IpcResult<SkuInfo>> {
    return this.request<SkuInfo>(IPC_CHANNELS.skuInfo, { sku });
  }

  setProductStatus(id: string, action: ProductAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "activate" ? IPC_CHANNELS.productActivate
      : action === "deactivate" ? IPC_CHANNELS.productDeactivate
        : IPC_CHANNELS.productArchive;
    return this.request<ActionResult>(channel, { id });
  }

  createCustomer(input: CreateCustomerInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.customerCreate, input);
  }

  setCustomerStatus(id: string, action: CustomerAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "activate" ? IPC_CHANNELS.customerActivate
      : action === "deactivate" ? IPC_CHANNELS.customerDeactivate
        : IPC_CHANNELS.customerArchive;
    return this.request<ActionResult>(channel, { id });
  }

  createSupplier(input: CreateSupplierInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.supplierCreate, input);
  }

  setSupplierStatus(id: string, action: SupplierAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "activate" ? IPC_CHANNELS.supplierActivate
      : action === "deactivate" ? IPC_CHANNELS.supplierDeactivate
        : action === "blacklist" ? IPC_CHANNELS.supplierBlacklist
          : IPC_CHANNELS.supplierArchive;
    return this.request<ActionResult>(channel, { id });
  }

  createSale(input: CreateSaleInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.salesCreate, input);
  }

  setSaleStatus(id: string, action: SalesAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "confirm" ? IPC_CHANNELS.salesConfirm : IPC_CHANNELS.salesCancel;
    return this.request<ActionResult>(channel, { id });
  }

  listSaleCustomers(): Promise<IpcResult<SalesCustomerList>> {
    return this.request<SalesCustomerList>(IPC_CHANNELS.salesCustomers);
  }

  createStock(input: CreateStockInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.inventoryCreate, input);
  }

  moveStock(id: string, action: InventoryAction, quantity: number): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.inventoryMove, { id, action, quantity });
  }

  createPurchase(input: CreatePurchaseInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.purchaseCreate, input);
  }

  setPurchaseStatus(id: string, action: PurchaseAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "approve" ? IPC_CHANNELS.purchaseApprove
      : action === "receive" ? IPC_CHANNELS.purchaseReceive
        : IPC_CHANNELS.purchaseCancel;
    return this.request<ActionResult>(channel, { id });
  }

  listPurchaseSuppliers(): Promise<IpcResult<PurchaseSupplierList>> {
    return this.request<PurchaseSupplierList>(IPC_CHANNELS.purchaseSuppliers);
  }

  createPriceList(input: CreatePriceListInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.pricingCreate, input);
  }

  addPriceToList(input: AddPriceInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.pricingAddPrice, input);
  }

  setPriceListStatus(id: string, action: PriceListAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "publish" ? IPC_CHANNELS.pricingPublish : IPC_CHANNELS.pricingArchive;
    return this.request<ActionResult>(channel, { id });
  }

  createShipment(input: CreateShipmentInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.logisticsCreate, input);
  }

  setShipmentStatus(id: string, action: ShipmentAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "dispatch" ? IPC_CHANNELS.logisticsDispatch
      : action === "deliver" ? IPC_CHANNELS.logisticsDeliver
        : IPC_CHANNELS.logisticsCancel;
    return this.request<ActionResult>(channel, { id });
  }

  createPublication(input: CreatePublicationInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.marketplaceCreate, input);
  }

  setPublicationStatus(id: string, action: PublicationAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "publish" ? IPC_CHANNELS.marketplacePublish
      : action === "pause" ? IPC_CHANNELS.marketplacePause
        : action === "resume" ? IPC_CHANNELS.marketplaceResume
          : IPC_CHANNELS.marketplaceDelete;
    return this.request<ActionResult>(channel, { id });
  }

  syncPublicationToMl(id: string): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.marketplaceSyncMl, { id });
  }

  importMlOrders(): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.marketplaceImportOrders);
  }

  /** Trae las publicaciones reales de Mercado Libre al catálogo local. */
  importMlListings(): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.marketplaceImportListings);
  }

  registerModel(input: RegisterModelInput): Promise<IpcResult<ActionResult>> {
    return this.request<ActionResult>(IPC_CHANNELS.aiRegister, input);
  }

  setModelStatus(id: string, action: ModelAction): Promise<IpcResult<ActionResult>> {
    const channel = action === "activate" ? IPC_CHANNELS.aiActivate : IPC_CHANNELS.aiRetire;
    return this.request<ActionResult>(channel, { id });
  }
}
